import { useEffect, useRef, useState } from 'react';

export type DrawerState = 'expanded' | 'expanding' | 'collapsed' | 'collapsing';

interface UseSideDrawerStateProps {
    collapsedWidth: number;
    expandedWidth: number;
    initialPinned?: boolean;
}

interface UseSideDrawerStateReturn {
    drawerState: DrawerState;
    drawerRef: React.MutableRefObject<HTMLDivElement | undefined>;
}

const TOLERANCE = 5;

const isWithinRange = (width: number, targetWidth: number): boolean => {
    return width >= targetWidth - TOLERANCE && width <= targetWidth + TOLERANCE;
};

export const useSideDrawerState = ({
    collapsedWidth,
    expandedWidth,
    initialPinned
}: UseSideDrawerStateProps): UseSideDrawerStateReturn => {
    const [drawerState, setDrawerState] = useState<DrawerState>(
        initialPinned ? 'expanded' : 'collapsed'
    );
    const drawerRef = useRef<HTMLDivElement>();

    useEffect(() => {
        const drawerElement: HTMLDivElement | undefined = drawerRef.current;

        if (!drawerElement) {
            return;
        }

        // transitionstart/transitionend bubble, so a descendant animating its
        // own `width` (e.g. a progress bar fill) would otherwise be mistaken
        // for the drawer itself expanding/collapsing. Only react to width
        // transitions that originate on the drawer element.
        const isOwnWidthTransition = (event: TransitionEvent): boolean =>
            event.target === drawerElement && event.propertyName === 'width';

        function handleTransitionStart(event: TransitionEvent) {
            if (isOwnWidthTransition(event)) {
                const currentWidth = parseFloat(
                    window.getComputedStyle(drawerElement as HTMLDivElement)
                        .width
                );

                // Determine if we're expanding or collapsing based on current width
                const isCurrentlyCollapsed = isWithinRange(
                    currentWidth,
                    collapsedWidth
                );
                const isCurrentlyExpanded = isWithinRange(
                    currentWidth,
                    expandedWidth
                );

                if (isCurrentlyCollapsed) {
                    setDrawerState('expanding');
                } else if (isCurrentlyExpanded) {
                    setDrawerState('collapsing');
                }
            }
        }

        function handleTransitionEnd(event: TransitionEvent) {
            if (isOwnWidthTransition(event)) {
                const newWidth = parseFloat(
                    window.getComputedStyle(drawerElement as HTMLDivElement)
                        .width
                );

                // When browser is zoomed in/out, the drawer width differs slightly.
                // So we need to check if the new width is within the range of the expanded/collapsed width
                const isNewWidthExpanded = isWithinRange(
                    newWidth,
                    expandedWidth
                );
                const isNewWidthCollapsed = isWithinRange(
                    newWidth,
                    collapsedWidth
                );

                if (isNewWidthExpanded) {
                    setDrawerState('expanded');
                } else if (isNewWidthCollapsed) {
                    setDrawerState('collapsed');
                }
            }
        }

        drawerElement.addEventListener(
            'transitionstart',
            handleTransitionStart
        );
        drawerElement.addEventListener('transitionend', handleTransitionEnd);

        return () => {
            drawerElement.removeEventListener(
                'transitionstart',
                handleTransitionStart
            );
            drawerElement.removeEventListener(
                'transitionend',
                handleTransitionEnd
            );
        };
    }, [collapsedWidth, expandedWidth]);

    return {
        drawerState,
        drawerRef
    };
};
